import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { buildOfficerSelectOptions } from '@app/finxone-web-frontend/app/lib/utils/companies-house-officer.utils';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  CompaniesHouseOfficer,
  LookupType,
  LookupsCommonType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, catchError, map, of, throwError } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';
import { SupportChatsService } from '@app/finxone-web-frontend/app/lib/services/support-chats-service/support-chats.service';

@Injectable({
  providedIn: 'root',
})
export class DataSourceService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private supportChatsService = inject(SupportChatsService);
  private store = inject(Store);

  private baseUrl: string;
  private _dataSources: { [key: string]: LookupsCommonType[] } = {};
  private dataSourcesSubject = new BehaviorSubject<{ [key: string]: LookupsCommonType[] }>({});

  private customLookupHandlers: Partial<Record<LookupType, () => Observable<LookupsCommonType[]>>> = {
    [LookupType.SUPPORT_CHAT_SUBJECTS]: () =>
      this.supportChatsService.getSupportChatSubjects().pipe(
        map((response) =>
          response.result.map((subject) => ({
            label: subject.name,
            value: subject.id,
            metadata: {
              relatesToAccount: subject.relatesToAccount,
            },
          })),
        ),
      ),
    // Officers are already present in businessOnboardingFlow state once a company is selected
    [LookupType.COMPANIES_HOUSE_DIRECTORS]: () => {
      const formState = this.store.selectSnapshot(
        FormActionState.getFormActionStateWithId(FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW),
      );
      const officers = (formState?.formData?.['members'] as CompaniesHouseOfficer[]) ?? [];
      return of(buildOfficerSelectOptions(officers));
    },
  };

  constructor() {
    this.configService.getApi('account_service').subscribe((response) => {
      this.baseUrl = response;
    });
  }

  public get dataSources(): { [key: string]: LookupsCommonType[] } {
    return this._dataSources;
  }

  public get dataSources$() {
    return this.dataSourcesSubject.asObservable();
  }

  public getDataSourceByType(type: LookupType) {
    const handler = this.customLookupHandlers[type];
    const source$ = handler
      ? handler()
      : this.http.get<LookupsCommonType[]>(`${this.baseUrl}/lookups/${type}`);

    return source$.pipe(
      map((response) => {
        this.addToDataSourcesArray(response, type);
        return response;
      }),
      catchError((error) => {
        console.error('getDataSourceByType:', error);
        return throwError(() => error);
      }),
    );
  }

  private addToDataSourcesArray(response: LookupsCommonType[], type: string): void {
    this._dataSources[type] = response;
    this.dataSourcesSubject.next({ ...this._dataSources });
  }
}
